<template lang="html">
  <v-container class="pa-0">
    <v-dialog v-model="showInfoDialog"
              max-width="500" lazy>
      <v-card>
        <v-card-title primary-title>
          <div>
            <p>
              Automatically create load groups from this Repeat Load as per the schedule here.
            </p>
            <p>
              Loads scheduled here are added to the planner early Friday morning for the following week.
            </p>
          </div>
        </v-card-title>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSchedulerDialog"
              max-width="500" lazy>
      <v-card>
        <v-card-title primary-title>
         Add schedule
        </v-card-title>
        <v-card-text>
          <v-layout column>
            <v-flex>
              <v-text-field label="Time"
                            type="time"
                            v-model="dayScheduleTime"
                            :error-messages="errors.first('time')"
                            data-vv-as="Time"
                            data-vv-name="time"
                            v-validate="'required'">
              </v-text-field>
            </v-flex>
            <v-flex>
              <v-text-field label="Load count"
                            type="number"
                            v-model="dayScheduleTotalCount"
                            :error-messages="errors.first('loadCount')"
                            data-vv-as="Load count"
                            data-vv-name="count"
                            v-validate="'required'">
              </v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="accent"
                 :disabled="errors.any()"
                 @click="saveSchedule">Save</v-btn>
          <v-btn @click="closeScheduleDialog()">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-layout column>
      <v-flex>
        <v-card>
          <v-toolbar >
            <v-btn flat fab
                   @click="showInfoDialog = true"
                   color="primary">
              <v-icon>info</v-icon>
            </v-btn>
            <v-layout column>
              <v-toolbar-title>
                Auto-Scheduled Weekly Load Groups
              </v-toolbar-title>
              <v-list-tile-sub-title class="font-italic font-weight-light">
                {{loadCount}} weekly {{ loadCount | pluralize('load', { includeNumber: false }) }}
              </v-list-tile-sub-title>
            </v-layout>
            <v-spacer/>

            <v-toolbar-items v-show="isDirty">
              <v-btn flat color="accent"
                     :disabled="savingSchedule"
                     @click="resetSchedule">
                <v-layout column>
                  <v-icon>clear</v-icon>
                  Ignore
                </v-layout>
              </v-btn>
              <v-btn color="accent"
                     :loading="savingSchedule"
                     @click="saveScheduleUpdates">
                <v-layout column>
                  <v-icon>save</v-icon>
                  Save
                </v-layout>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>

          <v-list>
            <!-- Day Start -->
            <template v-for="loadGroup in loadGroups">
              <v-list-tile :key="`${loadGroup.day}.group`" class="list-hotfix">
                <v-list-tile-action>
                  <v-btn fab small flat
                         @click="openScheduleDialog(loadGroup.day)"
                         color="accent">
                    <v-icon>add</v-icon>
                  </v-btn>
                </v-list-tile-action>
                <v-list-tile-title>
                  <b>{{loadGroup.day}}</b>
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile v-for="(detail, dayIdx) in loadGroup.details"
                           :key="`${loadGroup.day}_${dayIdx}_day`"
                           class="pl-4">
                <v-list-tile-action>
                  <v-btn fab small
                         @click="deleteSchedule(loadGroup, detail)"
                         flat color="accent">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>
                   {{detail.scheduledPickupTime | formatScheduleTime}}
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    {{ detail.loadCount | pluralize('truck', { includeNumber: true }) }}
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider :key="`${loadGroup.day}_divider`"/>
            </template>
          </v-list>

        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import moment from 'moment';

export default {
  name: 'VScheduleRepeatLoads',
  props: {
    load: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    savingSchedule: false,
    loadingTemplate: false,
    showInfoDialog: false,
    showSchedulerDialog: false,
    dayScheduleTime: null,
    dayScheduleTotalCount: null,
    daySelected: null,
    loadGroups: [],
    isDirty: false,
  }),
  filters: {
    formatScheduleTime(scheduleTime) {
      return moment(scheduleTime, 'HH:mm:ss').format('h:mm:ss a');
    },
  },
  watch: {
    load() {
      this.initLoadGroups();
    },
    isDirty() {
      this.$emit('dirty', this.isDirty);
    },
  },
  computed: {
    _() {
      return _;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    loadCount() {
      let totalCount = 0;
      const details = _.flattenDeep(this.loadGroups.map(item => item.details));

      details.forEach((detail) => {
        totalCount += detail.loadCount;
      });

      return totalCount;
    },
  },
  methods: {
    async saveScheduleUpdates() {
      try {
        this.savingSchedule = true;
        const response = await API.company.scheduleLoad(
          {
            loadId: this.load.humanId,
            body: {
              loadGroups: this.loadGroups,
            },
          },
        );

        this.$emit('refresh', response.data);
        this.isDirty = false;
        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Schedule saved',
        });
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.savingSchedule = false;
      }
    },
    async saveSchedule() {
      const isValid = await this.$validator.validateAll();
      if (isValid) {
        const dayDetails = _.find(this.loadGroups, loadGroup => loadGroup.day === this.daySelected);
        const details = _.cloneDeep(dayDetails.details);
        details.push({
          loadCount: parseInt(this.dayScheduleTotalCount),
          scheduledPickupTime: moment(this.dayScheduleTime, 'HH:mm').format('HH:mm:ss'),
        });
        const newDetails = _.sortBy(details, detail => moment(detail.scheduledPickupTime, 'HH:mm:ss').unix());

        this.$set(dayDetails, 'details', newDetails);
        this.isDirty = true;
        this.closeScheduleDialog();
      }
    },
    closeScheduleDialog() {
      this.showSchedulerDialog = false;
      this.dayScheduleTime = null;
      this.dayScheduleTotalCount = null;
      this.$validator.reset();
    },
    openScheduleDialog(day) {
      this.daySelected = day;
      this.showSchedulerDialog = true;
    },
    resetSchedule() {
      this.initLoadGroups();
      this.isDirty = false;
    },
    deleteSchedule(loadGroup, schedule) {
      const group = _.find(this.loadGroups, item => item.day === loadGroup.day);
      group.details = _.reject(loadGroup.details, schedule);
      this.isDirty = true;
    },
    initLoadGroups() {
      this.loadGroups = _.cloneDeep(_.get(this.load, 'recurrenceSchedule.loadGroups', []));
    },
  },
  created() {
    this.initLoadGroups();
  },
};

</script>



// WEBPACK FOOTER //
// src/components/InfoWidgets/ScheduleRepeatLoads.vue